/**
 * useSlideImage - Lazy load images for slides
 * 
 * Only fetches image when slide is visible and has a pending image prompt.
 * Uses Wikimedia search first, falls back to AI generation.
 */

import { useState, useEffect, useRef } from 'react';
import { searchImage } from '@/components/spaces/services/explainerService';

interface UseSlideImageProps {
    slideId: string;
    imagePrompt?: string;
    existingUrl?: string;
    isVisible: boolean;
    journalId?: string;
}

interface UseSlideImageResult {
    imageUrl: string;
    isLoading: boolean;
    error: string | null;
}

export function useSlideImage({
    slideId,
    imagePrompt,
    existingUrl,
    isVisible,
    journalId,
}: UseSlideImageProps): UseSlideImageResult {
    // Initialize with existing URL - this is the persisted value
    const [imageUrl, setImageUrl] = useState(existingUrl || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Track which slides we've already attempted to fetch in this session
    const attemptedSlidesRef = useRef<Set<string>>(new Set());
    const lastSlideIdRef = useRef<string>(slideId);

    // Sync imageUrl with existingUrl when it changes (e.g., data loaded from DB)
    useEffect(() => {
        if (existingUrl && existingUrl !== imageUrl) {
            console.log(`📋 Slide ${slideId}: Loading persisted image: ${existingUrl.substring(0, 50)}...`);
            setImageUrl(existingUrl);
        }
    }, [existingUrl, slideId]);

    // Reset state when slideId changes
    useEffect(() => {
        if (lastSlideIdRef.current !== slideId) {
            lastSlideIdRef.current = slideId;
            // Reset to existing URL or empty for new slide
            setImageUrl(existingUrl || '');
            setIsLoading(false);
            setError(null);
        }
    }, [slideId, existingUrl]);

    // Fetch image when conditions are met
    useEffect(() => {
        // Skip if already have a persisted URL (check existingUrl directly from props)
        if (existingUrl) {
            return;
        }

        // Don't fetch if:
        // - Not visible
        // - Already have a URL in state (previously fetched this session)
        // - No prompt to search with
        // - Already attempted this specific slide
        const alreadyAttempted = attemptedSlidesRef.current.has(slideId);

        if (!isVisible || imageUrl || !imagePrompt || alreadyAttempted) {
            return;
        }

        // Mark this slide as attempted
        attemptedSlidesRef.current.add(slideId);
        setIsLoading(true);
        setError(null);

        console.log(`🖼️ Fetching image for slide ${slideId}: "${imagePrompt}"`);

        searchImage(imagePrompt, journalId || '')
            .then(result => {
                // Only update if we're still on the same slide
                if (lastSlideIdRef.current === slideId) {
                    if (result?.url) {
                        console.log(`✅ Got image for slide ${slideId}: ${result.url.substring(0, 50)}...`);
                        setImageUrl(result.url);
                    } else {
                        setError('No image found');
                    }
                }
            })
            .catch(err => {
                console.error(`Failed to load image for slide ${slideId}:`, err);
                if (lastSlideIdRef.current === slideId) {
                    setError('Failed to load image');
                }
            })
            .finally(() => {
                if (lastSlideIdRef.current === slideId) {
                    setIsLoading(false);
                }
            });
    }, [isVisible, imageUrl, imagePrompt, slideId, journalId, existingUrl]);

    return { imageUrl, isLoading, error };
}

